import { Component } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';

export interface StatementPeriodDialogResult {
    startDate: string;
    endDate: string;
}

function dateRangeValidator(maxDate: Date): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const startDate = control.get('startDate')?.value;
        const endDate = control.get('endDate')?.value;

        if (!startDate || !endDate) {
            return null;
        }

        const start = new Date(startDate);
        const end = new Date(endDate);
        const today = new Date(maxDate);
        today.setHours(23, 59, 59, 999);

        if (start > end) {
            return { dateOrder: true };
        }

        if (start > today || end > today) {
            return { futureDate: true };
        }

        return null;
    };
}

@Component({
    selector: 'app-statement-period-dialog',
    templateUrl: './statement-period-dialog.component.html',
    styleUrls: ['./statement-period-dialog.component.scss']
})
export class StatementPeriodDialogComponent {
    readonly today = new Date();
    readonly form: FormGroup;

    constructor(
        private readonly fb: FormBuilder,
        private readonly dialogRef: MatDialogRef<StatementPeriodDialogComponent, StatementPeriodDialogResult>
    ) {
        const monthStart = new Date(this.today.getFullYear(), this.today.getMonth(), 1);
        this.form = this.fb.group({
            startDate: [monthStart, Validators.required],
            endDate: [this.today, Validators.required]
        }, { validators: [dateRangeValidator(this.today)] });
    }

    cancel(): void {
        this.dialogRef.close();
    }

    download(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        const values = this.form.getRawValue();
        this.dialogRef.close({
            startDate: this.toDateParam(values.startDate),
            endDate: this.toDateParam(values.endDate)
        });
    }

    private toDateParam(value: Date | string): string {
        const date = new Date(value);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }
}
